import axios from 'axios';
import { authService } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

function authHeaders() {
  const token = authService.getToken();
  return token
    ? { 'x-access-token': token, 'x-pp-client-id': 'privacypal-user-portal' }
    : {};
}

// The catalog stores ONE-WAY HASHES only — no plaintext value is ever returned
// by the API. Rows carry hashes + metadata for display.
export interface BusinessDataSensitive {
  id: string;
  entityType: string;
  category: string;
  sourceType: string;
  sourceId: string;
  sourceObject?: string | null;
  fieldApiName?: string | null;
  confidence: number;
  valueHash: string;
  variantHashes: string[];
  firstSeenAt: string | null;
  lastRebuiltAt: string | null;
}

export interface BusinessDataOperation {
  connectionId: string;
  serviceType: string;
  status: 'running' | 'completed' | 'error';
  startedAt: string | null;
  completedAt: string | null;
  itemsFound: number;
  error: string | null;
  lastSuccessAt: string | null;
  lastSuccessItems: number | null;
}

export interface BusinessDataIndexMetadata {
  lastSyncAt: string | null;
  entryCount: number;
}

export interface BusinessDataCatalogResponse {
  catalog: BusinessDataSensitive[];
  metadata: BusinessDataIndexMetadata;
  scanJobs: BusinessDataOperation[];
}

class BusinessDataService {
  async getCatalog(): Promise<BusinessDataCatalogResponse> {
    const res = await axios.get(`${API_BASE_URL}/api/business-data/catalog`, {
      headers: authHeaders(),
    });
    return res.data;
  }

  async scanConnection(connectionId: string): Promise<void> {
    await axios.post(
      `${API_BASE_URL}/api/business-data/scan`,
      { connectionId },
      { headers: authHeaders() }
    );
  }

  async syncAll(): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/business-data/sync`, {}, { headers: authHeaders() });
  }

  async getTerms(): Promise<{ valueHash: string; entityType: string; confidence: number }[]> {
    const res = await axios.get(`${API_BASE_URL}/api/business-data/terms`, { headers: authHeaders() });
    return res.data?.terms || [];
  }
}

export const businessDataService = new BusinessDataService();
